import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Smartphone,
  Wifi,
  Crown,
  CreditCard,
  Terminal,
  FileKey,
  Flag,
  Activity,
  ArrowUpRight,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card.js';
import { CommandStatusBadge, RoleBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { ErrorState } from '../../components/ui/EmptyState.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchOverviewMetrics, fetchSystemHealth } from '../../api/system.js';
import { formatRelativeTime, truncateId } from '../../lib/utils.js';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

export function DashboardPage() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const {
    data: metrics,
    isLoading: isLoadingMetrics,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: ['overview-metrics'],
    queryFn: fetchOverviewMetrics,
    refetchInterval: 30000, // Refresh every 30s
  });

  const { data: health, isLoading: isLoadingHealth } = useQuery({
    queryKey: ['system-health'],
    queryFn: fetchSystemHealth,
    refetchInterval: 45000,
  });

  if (isLoadingMetrics || isLoadingHealth) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (metricsError || !metrics) {
    return (
      <ErrorState
        title={t('dashboard.failedToLoad')}
        message={metricsError?.message}
        onRetry={() => refetchMetrics()}
      />
    );
  }

  const statCards = [
    { label: t('dashboard.totalUsers'), value: metrics.total_users.toLocaleString(), icon: Users, path: '/admin/users', color: 'text-indigo-600 dark:text-indigo-400' },
    { label: t('dashboard.activeDevices'), value: metrics.active_devices.toLocaleString(), icon: Smartphone, path: '/admin/devices', color: 'text-blue-600 dark:text-blue-400' },
    { label: t('dashboard.onlineDevices'), value: metrics.online_devices.toLocaleString(), icon: Wifi, path: '/admin/devices', color: 'text-emerald-600 dark:text-emerald-400' },
    { label: t('dashboard.premiumUsers'), value: metrics.premium_users.toLocaleString(), icon: Crown, path: '/admin/entitlements', color: 'text-amber-500' },
    { label: t('dashboard.subscriptions'), value: metrics.active_subscriptions.toLocaleString(), icon: CreditCard, path: '/admin/subscriptions', color: 'text-purple-600 dark:text-purple-400' },
    { label: t('dashboard.pendingCommands'), value: metrics.pending_commands.toString(), icon: Terminal, path: '/admin/commands', color: metrics.pending_commands > 0 ? 'text-amber-600' : 'text-slate-400' },
    { label: t('dashboard.activeSnapshots'), value: metrics.active_snapshots.toString(), icon: FileKey, path: '/admin/snapshots', color: 'text-rose-500' },
    { label: t('dashboard.featureFlagsOn'), value: metrics.feature_flags_enabled.toString(), icon: Flag, path: '/admin/features', color: 'text-teal-600 dark:text-teal-400' },
  ];

  const healthServices = [
    { name: t('system.apiWorker'), status: health?.api || 'healthy' },
    { name: t('system.d1Db'), status: health?.d1 || 'healthy' },
    { name: t('system.kvConfig'), status: health?.kv || 'healthy' },
    { name: t('system.r2Storage'), status: health?.r2 || 'healthy' },
    { name: t('system.queues'), status: health?.queues || 'healthy' },
    { name: t('system.cronWorker'), status: health?.cron || 'healthy' },
  ];

  const COLORS = ['#6366f1', '#38bdf8', '#a855f7', '#f59e0b'];

  return (
    <div className="space-y-6">
      {/* Quick Action Banner */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl border border-indigo-100 dark:border-indigo-950/60 bg-gradient-to-r from-indigo-50/50 via-white to-slate-50 dark:from-indigo-950/20 dark:via-slate-900/50 dark:to-slate-900/20">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {t('dashboard.bannerTitle')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {t('dashboard.bannerDesc')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/commands')}>
            <Terminal className="w-3.5 h-3.5 mr-1.5" />
            {t('dashboard.dispatchCommand')}
          </Button>
          <Button size="sm" variant="primary" onClick={() => navigate('/admin/devices')}>
            <Smartphone className="w-3.5 h-3.5 mr-1.5" />
            {t('dashboard.inspectFleet')}
          </Button>
        </div>
      </div>

      {/* Primary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Card
              key={i}
              onClick={() => navigate(stat.path)}
              className="cursor-pointer hover:border-indigo-500/40 dark:hover:border-indigo-500/40 transition-all hover:shadow-md group"
            >
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-1">
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  {stat.label}
                </span>
                <Icon className={`w-4 h-4 ${stat.color} transition-transform group-hover:scale-110`} />
              </CardHeader>
              <CardContent className="pt-2">
                <div className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {stat.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* System Health Strip */}
      <Card>
        <CardHeader className="flex-row items-center justify-between pb-2">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-500" />
            <CardTitle>{t('dashboard.infraHealthTitle')}</CardTitle>
          </div>
          <span className="text-xs text-slate-400 font-mono">
            {t('dashboard.checkedAgo', { time: formatRelativeTime(health?.last_checked) })}
          </span>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {healthServices.map((svc, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 text-xs"
              >
                <span className="font-medium text-slate-700 dark:text-slate-300">{svc.name}</span>
                <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  {svc.status}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Devices Timeline */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('dashboard.chartActiveDevices')}</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={metrics.charts.active_devices_timeline}>
                <defs>
                  <linearGradient id="colorIos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorAndroid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} />
                <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#fff',
                  }}
                />
                <Area type="monotone" dataKey="ios" name="iOS" stroke="#6366f1" fillOpacity={1} fill="url(#colorIos)" />
                <Area type="monotone" dataKey="android" name="Android" stroke="#38bdf8" fillOpacity={1} fill="url(#colorAndroid)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Platform Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.chartPlatforms')}</CardTitle>
          </CardHeader>
          <CardContent className="h-72 flex flex-col items-center justify-center">
            <ResponsiveContainer width="100%" height="75%">
              <PieChart>
                <Pie
                  data={metrics.charts.platforms_distribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={5}
                  dataKey="count"
                  nameKey="platform"
                >
                  {metrics.charts.platforms_distribution.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#fff',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex gap-4 text-xs">
              {metrics.charts.platforms_distribution.map((p, idx) => (
                <div key={p.platform} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                  <span className="text-slate-600 dark:text-slate-400">{p.platform}: {p.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Commands */}
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>{t('dashboard.recentFleetCommands')}</CardTitle>
            <Button size="sm" variant="ghost" onClick={() => navigate('/admin/commands')}>
              {t('common.viewAll')} <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {metrics.recent_activity.commands.map((cmd) => (
                <div key={cmd.id} className="py-2.5 flex items-center justify-between gap-3 text-xs">
                  <div>
                    <div className="font-mono text-slate-800 dark:text-slate-200 font-medium">
                      {t(`commandTypes.${cmd.type}`) !== `commandTypes.${cmd.type}` ? t(`commandTypes.${cmd.type}`) : cmd.type}
                    </div>
                    <div className="text-[11px] text-slate-400 font-mono">
                      {t('dashboard.deviceLabel')}: {truncateId(cmd.device_id, 6)} • {formatRelativeTime(cmd.created_at)}
                    </div>
                  </div>
                  <CommandStatusBadge status={cmd.status} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Audit Events */}
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>{t('dashboard.recentAuditEvents')}</CardTitle>
            <Button size="sm" variant="ghost" onClick={() => navigate('/admin/audit')}>
              {t('common.viewAll')} <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {metrics.recent_activity.audit_events.map((aud) => (
                <div key={aud.id} className="py-2.5 flex items-center justify-between gap-3 text-xs">
                  <div>
                    <div className="font-semibold text-slate-800 dark:text-slate-200">
                      {aud.action}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {t('dashboard.targetLabel')}: {aud.target_type} ({truncateId(aud.target_id, 6)}) • {formatRelativeTime(aud.created_at)}
                    </div>
                  </div>
                  <RoleBadge role={aud.actor_role as any} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
